import { Flex } from 'foundations-components/components/Flex';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Popover } from 'foundations-components/transitional/components/Popover';
import { useState, useCallback } from 'react';
import { useReactNodeView } from '../utils/ReactNodeView';
import styled, { css } from 'styled-components';
import { getTokenTooltip } from '../utils/personalizationTokenUtils';
import { PersonalizationTokenPopover } from '../commands/InsertPersonalizationToken';
import { CompaniesIcon } from 'foundations-assets/icons/components/CompaniesIcon';
import { ContactsIcon } from 'foundations-assets/icons/components/ContactsIcon';
import { MeetingsIcon } from 'foundations-assets/icons/components/MeetingsIcon';
import { WarningIcon } from 'foundations-assets/icons/components/WarningIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const personalizationTokenIconMap = {
  companies: CompaniesIcon,
  contacts: ContactsIcon,
  meetings: MeetingsIcon
};
export const StyledToken = styled.span.withConfig({
  displayName: "PersonalizationTokenView__StyledToken"
})(["align-items:center;background-color:", ";border:", " solid ", ";border-radius:", ";color:", ";cursor:default;display:inline-flex;line-height:1;margin:", ";padding:", ";vertical-align:middle;user-select:text;white-space:nowrap;inline-size:auto;", ""], ({
  theme,
  backgroundColor
}) => backgroundColor || theme.color['fill-info-subtle'], ({
  theme
}) => theme.borderWidth['100'], ({
  theme,
  borderColor
}) => borderColor || theme.color['border-core-default'], ({
  theme
}) => theme.borderRadius['100'], ({
  theme,
  fontColor
}) => fontColor || theme.color['fill-info-default'], ({
  theme,
  margin
}) => margin || theme.space['0'], ({
  theme,
  padding
}) => padding || `${theme.space['100']} ${theme.space['200']}`, ({
  theme,
  $isError
}) => $isError && css(["background-color:", ";border-color:transparent;color:", ";text-decoration:underline dashed;text-decoration-color:", ";text-underline-offset:0.2em;"], theme.color['fill-caution-subtle'], theme.color['text-core-default'], theme.color['border-caution-default']));
const getRenderedDisplayText = ({
  displayText,
  tokenKey,
  defaultTokenValue
}) => {
  const baseText = displayText || tokenKey;
  return defaultTokenValue ? `${baseText} (${defaultTokenValue})` : baseText;
};
export function PersonalizationTokenView() {
  var _node$attrs;
  const {
    node,
    editorConfig,
    view,
    getPos
  } = useReactNodeView();
  const [open, setOpen] = useState(false);
  const {
    tokenKey,
    defaultTokenValue,
    tokenStyles,
    tokenMeta,
    displayText,
    error,
    errorMessage
  } = (_node$attrs = node === null || node === void 0 ? void 0 : node.attrs) !== null && _node$attrs !== void 0 ? _node$attrs : {};
  const tokenIconKey = tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.tokenIconKey;
  const isError = Boolean(error);
  const tooltipConfig = getTokenTooltip(editorConfig, tokenKey);
  const onOpenChange = useCallback(e => {
    if (e.target) {
      setOpen(e.target.value);
    }
  }, [setOpen]);
  const handleClick = useCallback(() => {
    // Unresolved (missing) snippet tokens are read-only — no edit popover.
    if (!(view !== null && view !== void 0 && view.editable) || isError) return;
    setOpen(prevState => !prevState);
  }, [setOpen, view, isError]);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  const personalizationTokenPopover = useCallback(() => /*#__PURE__*/_jsx(PersonalizationTokenPopover, {
    initialTokenKey: tokenKey,
    initialDefaultValue: defaultTokenValue,
    initialTokenMeta: tokenMeta,
    nodePos: typeof getPos === 'function' ? getPos() : undefined,
    onPopoverConfirm: handleClose
  }), [tokenKey, defaultTokenValue, tokenMeta, getPos, handleClose]);
  const tokenContent =
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsxs(StyledToken, {
    backgroundColor: tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.backgroundColor,
    borderColor: tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.borderColor,
    fontColor: tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.fontColor,
    margin: tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.margin,
    padding: tokenStyles === null || tokenStyles === void 0 ? void 0 : tokenStyles.padding,
    $isError: isError,
    "data-test-id": "ProseMirror__personalizationToken",
    onClick: handleClick,
    style: {
      cursor: view !== null && view !== void 0 && view.editable && !isError ? 'pointer' : 'default'
    },
    children: [personalizationTokenIconMap[tokenIconKey] && /*#__PURE__*/_jsx(IconHolder, {
      icon: personalizationTokenIconMap[tokenIconKey],
      marginInlineEnd: "200",
      padding: "0"
    }), (tooltipConfig === null || tooltipConfig === void 0 ? void 0 : tooltipConfig.icon) && !isError && /*#__PURE__*/_jsx(IconHolder, {
      icon: tooltipConfig.icon,
      marginInlineEnd: "200",
      padding: "0"
    }), getRenderedDisplayText({
      displayText,
      tokenKey,
      defaultTokenValue
    }), isError && errorMessage && /*#__PURE__*/_jsx(Tooltip, {
      title: errorMessage,
      placement: "top",
      children: /*#__PURE__*/_jsx(Flex, {
        as: "span",
        alignItems: "center",
        "data-test-id": "ProseMirror__personalizationToken__errorIcon",
        children: /*#__PURE__*/_jsx(IconHolder, {
          icon: WarningIcon,
          marginInlineStart: "200",
          padding: "0"
        })
      })
    })]
  });
  const wrappedTokenContent = tooltipConfig && !isError ? /*#__PURE__*/_jsx(Tooltip, {
    title: tooltipConfig.title,
    placement: "top",
    children: tokenContent
  }) : tokenContent;

  // Missing tokens are read-only: render without the edit popover wrapper.
  if (isError) {
    return /*#__PURE__*/_jsx("span", {
      "data-test-id": "ProseMirror__personalizationToken__wrapper",
      children: wrappedTokenContent
    });
  }
  return /*#__PURE__*/_jsx(Popover, {
    open: open,
    onOpenChange: onOpenChange,
    Content: personalizationTokenPopover,
    children: /*#__PURE__*/_jsx("span", {
      "data-test-id": "ProseMirror__personalizationToken__wrapper",
      children: wrappedTokenContent
    })
  });
}
PersonalizationTokenView.displayName = 'PersonalizationTokenView';
export function personalizationTokenViewUpdateFn({
  previousNode,
  node
}) {
  if (previousNode.attrs.tokenKey !== node.attrs.tokenKey || previousNode.attrs.defaultTokenValue !== node.attrs.defaultTokenValue || previousNode.attrs.displayText !== node.attrs.displayText || previousNode.attrs.error !== node.attrs.error || previousNode.attrs.errorMessage !== node.attrs.errorMessage || JSON.stringify(previousNode.attrs.tokenMeta) !== JSON.stringify(node.attrs.tokenMeta)) {
    return false;
  }
  return true;
}